import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Monitor,
  KeyRound,
  Package,
  BarChart3,
  Settings,
  Tag,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Loader2,
  Archive,
  Cloud,
  FileSearch,
  Briefcase,
  Bell,
  UserCog,
  Search,
  Sun,
  Moon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { authService } from "../../services/authService";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../hooks/useAuth";
import { useSessionResume } from "../../hooks/useSessionResume";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// ─── Nav type definitions ────────────────────────────────────────────────────

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  /** Backend permission slug required to see this item. Omit = always visible. */
  requiredPermission?: string;
  /** Visible if the user holds ANY of these slugs (used for multi-tier items). */
  requiredAnyPermission?: string[];
}

interface NavGroup {
  label: string;
  icon: React.ElementType;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(item: NavEntry): item is NavGroup {
  return "children" in item;
}

// ─── Navigation definition (permission-slug gated) ───────────────────────────
// Each item's `requiredPermission` must match a slug in the user's permissions[].
// This mirrors the backend @Permissions() decorator exactly.

const navigation: NavEntry[] = [
  // Org dashboard — only for users with a dashboard scope (global or department).
  // Standard Users (no dashboard.view.*) land on My Portfolio instead.
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", requiredAnyPermission: ["dashboard.view.all", "dashboard.view.department"] },
  { label: "My Portfolio", icon: Briefcase, path: "/dashboard/profile" },
  { label: "Assets",    icon: Monitor,         path: "/dashboard/assets",    requiredPermission: "assets.view" },
  // Manual-entry cloud register — nothing here is synced from a provider.
  { label: "Cloud Assets", icon: Cloud,        path: "/dashboard/cloud-assets", requiredPermission: "cloud.view" },
  { label: "Licenses",  icon: KeyRound,        path: "/dashboard/licenses",  requiredPermission: "licenses.view" },
  // Gates on inventory-mgmt.view: /dashboard/inventory renders InventoryManagementModule,
  // which calls /api/inventory-management/* exclusively. The inventory.* slugs belong to
  // the separate catalog/stock module.
  { label: "Inventory", icon: Archive,         path: "/dashboard/inventory", requiredPermission: "inventory-mgmt.view" },
  { label: "Analytics", icon: BarChart3,       path: "/dashboard/analytics", requiredPermission: "reports.view" },
  // A single flat link, not a submenu: the Audit page's tabs (Overview, Asset,
  // License, Inventory, Cloud Activity, Cloud Audit) are plain in-page state
  // with nothing in the URL, so there is no distinct destination per tab to
  // deep-link a sub-item at — every one of them would open the same place.
  { label: "Audit", icon: FileSearch, path: "/dashboard/audit-report", requiredPermission: "reports.view" },
  {
    label: "Admin",
    icon: Settings,
    children: [
      // Users, User Setup (Departments), and Access Setup (Roles/Permissions) are
      // consolidated into one page (UserAccessSetupPage) with flat tabs, same
      // pattern as Asset Setup — this nav item covers all of it.
      { label: "User & Access",        icon: UserCog,       path: "/dashboard/admin/user-access-setup",    requiredAnyPermission: ["users.manage", "users.create", "users.edit", "departments.manage", "roles.view"] },
      { label: "Asset Setup",          icon: Tag,           path: "/dashboard/admin/asset-setup",          requiredAnyPermission: ["categories.manage", "brands.manage", "vendors.manage", "assets.manage"] },
      { label: "Cloud Setup",          icon: Cloud,         path: "/dashboard/admin/cloud-setup",          requiredPermission: "cloud.manage" },
      { label: "Inventory Setup",      icon: Package,       path: "/dashboard/admin/inventory-setup",      requiredPermission: "inventory-mgmt.manage" },
      { label: "License Setup",        icon: KeyRound,      path: "/dashboard/admin/license-setup",        requiredPermission: "licenses.manage" },
      { label: "System Settings",      icon: Settings,      path: "/dashboard/admin/settings",             requiredPermission: "settings.manage" },
      { label: "Notifications",        icon: Bell,          path: "/dashboard/admin/notifications",        requiredPermission: "settings.manage" },
    ],
  },
];

// ─── Breadcrumb labels ───────────────────────────────────────────────────────
// Path-segment → human label. Segments not listed fall back to Title Case.

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  profile: "My Portfolio",
  assets: "Assets",
  licenses: "Licenses",
  inventory: "Inventory",
  analytics: "Analytics",
  "audit-report": "Audit",
  audit: "Audit",
  admin: "Admin",
  "user-access-setup": "User & Access",
  "asset-setup": "Asset Setup",
  "cloud-assets": "Cloud Assets",
  "cloud-setup": "Cloud Setup",
  "inventory-setup": "Inventory Setup",
  "license-setup": "License Setup",
  settings: "Settings",
  notifications: "Notifications",
};

const segmentLabel = (seg: string): string =>
  SEGMENT_LABELS[seg] ??
  (/^\d+$/.test(seg)
    ? `#${seg}`
    : seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  // useAuth reads permission slugs from localStorage — synced with the backend PermissionsGuard
  const { hasPermission, hasAnyPermission, roleName } = useAuth();
  const { availableCurrencies, selectedCurrency, setCurrency, reloadCurrencies } = useCurrency();
  const { theme, toggleTheme } = useTheme();
  // Story 112: re-validate the session as soon as a long-backgrounded tab
  // regains focus, instead of surfacing the expiry on the user's next click.
  useSessionResume();

  const [collapsed,      setCollapsed]      = useState(false);
  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["Admin", "Audit"]);
  const [profile,        setProfile]        = useState<any>(null);
  const [loadingUser,    setLoadingUser]    = useState(true);
  const [searchOpen,     setSearchOpen]     = useState(false);

  useEffect(() => {
    authService.getProfile()
      .then(setProfile)
      .catch((err) => console.error("Failed to load user profile", err))
      .finally(() => setLoadingUser(false));
    // AppShell only mounts once authenticated — fetch the auth-guarded currency
    // list here (the fetch on app mount happens on the login screen and 401s).
    reloadCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⌘K / Ctrl+K opens the global navigation search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // The sidebar collapses via a CSS width transition (w-64 -> w-16, 200ms), not
  // a remount, so ResizeObserver-based redraw (e.g. ApexCharts' own default)
  // doesn't reliably catch the resulting reflow within the transition window.
  // A window "resize" event after the transition settles is what every chart's
  // redrawOnWindowResize listener already expects, so nudge it explicitly.
  useEffect(() => {
    const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
    return () => clearTimeout(timer);
  }, [collapsed]);

  const handleLogout = async () => {
    await authService.logout();
    navigate("/login");
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    const [pathOnly, query] = path.split("?");
    if (query) {
      return location.pathname === pathOnly && location.search === `?${query}`;
    }
    if (pathOnly === "/dashboard/audit-report") {
      // Bare "Audit Overview" link should only match when there's no tab query
      return location.pathname === pathOnly && !location.search;
    }
    return location.pathname.startsWith(pathOnly);
  };

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  // ── Permission-based nav filtering ──────────────────────────────────────────
  /** Is a flat nav item visible to this user? */
  const isItemVisible = (item: NavItem): boolean => {
    if (item.requiredAnyPermission) return hasAnyPermission(item.requiredAnyPermission);
    return !item.requiredPermission || hasPermission(item.requiredPermission);
  };

  /** Build the filtered nav list — keeps only items the user can access. */
  const filteredNav: NavEntry[] = navigation.reduce<NavEntry[]>((acc, entry) => {
    if (isNavGroup(entry)) {
      const visibleChildren = entry.children.filter(isItemVisible);
      // Only show the Admin group if at least one child is accessible
      if (visibleChildren.length > 0) {
        acc.push({ ...entry, children: visibleChildren });
      }
    } else if (isItemVisible(entry)) {
      acc.push(entry);
    }
    return acc;
  }, []);

  // Flat list of every reachable page — feeds the ⌘K palette
  const searchTargets = useMemo(
    () =>
      filteredNav.flatMap((entry) =>
        isNavGroup(entry)
          ? entry.children.map((c) => ({ ...c, group: entry.label }))
          : [{ ...entry, group: "Navigate" }]
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  // Breadcrumb trail from the current path (skips the /dashboard root itself)
  const breadcrumbs = useMemo(() => {
    const segs = location.pathname.split("/").filter(Boolean);
    return segs.map((seg, i) => ({
      label: segmentLabel(seg),
      path: "/" + segs.slice(0, i + 1).join("/"),
      last: i === segs.length - 1,
    }));
  }, [location.pathname]);

  const navLinkClass = (active: boolean, extra?: string) =>
    cn(
      "group flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors no-underline",
      active
        ? "bg-sidebar-accent text-sidebar-accent-foreground"
        : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      extra
    );

  // ── Sidebar ──────────────────────────────────────────────────────────────────
  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className={cn("flex h-14 items-center px-4", collapsed && "justify-center px-2")}>
        <Link to="/dashboard" className="flex items-center gap-2 no-underline" onClick={() => setMobileOpen(false)}>
          {collapsed ? (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm shadow-sm">
              IT
            </div>
          ) : (
            <img src="/logo.svg" alt="IT Asset Management" className="h-8 object-contain" />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-3">
        <nav className="flex flex-col gap-0.5">
          {filteredNav.map((item) =>
            isNavGroup(item) ? (
              <div key={item.label} className="mt-3">
                {collapsed ? (
                  // Collapsed sidebar: show only child icons
                  item.children.map((child) => (
                    <TooltipProvider key={child.path} delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={child.path}
                            onClick={() => setMobileOpen(false)}
                            className={navLinkClass(isActive(child.path), "w-full justify-center px-0")}
                          >
                            <child.icon className="h-4 w-4" strokeWidth={1.75} />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right">{child.label}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))
                ) : (
                  <>
                    <button
                      onClick={() => toggleGroup(item.label)}
                      className="flex h-7 w-full items-center gap-2 rounded-md px-2.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/70 hover:text-sidebar-accent-foreground transition-colors cursor-pointer"
                    >
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 transition-transform",
                          expandedGroups.includes(item.label) && "rotate-180"
                        )}
                      />
                    </button>
                    {expandedGroups.includes(item.label) && (
                      <div className="mt-0.5 flex flex-col gap-0.5">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileOpen(false)}
                            className={navLinkClass(isActive(child.path))}
                          >
                            <child.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <TooltipProvider key={item.path} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={navLinkClass(isActive(item.path), collapsed ? "justify-center px-0" : undefined)}
                    >
                      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            )
          )}
        </nav>
      </ScrollArea>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden lg:block border-t border-sidebar-border p-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center text-sidebar-foreground"
          onClick={() => setCollapsed(!collapsed)}
        >
          <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          {!collapsed && <span className="ml-2">Collapse</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — Mobile */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform border-r border-sidebar-border bg-sidebar transition-transform duration-200 lg:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="absolute right-2 top-3">
          <Button variant="ghost" size="icon-sm" onClick={() => setMobileOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        {sidebarContent}
      </aside>

      {/* Sidebar — Desktop */}
      <aside
        className={cn(
          "hidden lg:flex lg:flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
          collapsed ? "w-16" : "w-60"
        )}
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Compact logo — mobile only (sidebar is hidden there) */}
          <Link to="/dashboard" className="flex items-center no-underline sm:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
              IT
            </div>
          </Link>

          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="hidden sm:flex min-w-0 items-center gap-1 text-sm">
            {breadcrumbs.map((crumb) => (
              <React.Fragment key={crumb.path}>
                {crumb.path !== "/dashboard" && (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                )}
                {crumb.last ? (
                  <span className="truncate font-medium text-foreground">{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="truncate text-muted-foreground transition-colors hover:text-foreground no-underline"
                  >
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="flex-1" />

          {/* Global search (⌘K) */}
          <button
            onClick={() => setSearchOpen(true)}
            className="hidden md:inline-flex h-9 w-56 items-center gap-2 rounded-md border bg-card px-3 text-sm text-muted-foreground shadow-xs transition-colors hover:bg-accent cursor-pointer"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              Ctrl K
            </kbd>
          </button>
          <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setSearchOpen(true)}>
            <Search className="h-4 w-4" />
          </Button>

          {/* Theme toggle */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={toggleTheme} aria-label="Toggle theme">
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Switch to {theme === "dark" ? "light" : "dark"} mode</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Global display-currency switcher — presentation only, storage stays in base currency */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Was `hidden md:inline-flex`, which left phones with no way to
                  change display currency at all. Now it shrinks to the symbol
                  below md instead of disappearing. */}
              <Button
                variant="outline"
                size="sm"
                aria-label={`Display currency: ${selectedCurrency.code}. Change currency`}
                className="inline-flex gap-1 px-2 font-medium md:h-9 md:px-3"
              >
                <span>{selectedCurrency.symbol}</span>
                <span className="hidden md:inline">{selectedCurrency.code}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40 max-h-72 overflow-y-auto">
              {availableCurrencies.map((c) => (
                <DropdownMenuItem
                  key={c.code}
                  onClick={() => setCurrency(c.code)}
                  className={cn(
                    "cursor-pointer gap-2",
                    c.code === selectedCurrency.code && "bg-accent font-semibold"
                  )}
                >
                  <span className="w-8 text-muted-foreground">{c.symbol}</span>
                  <span>{c.code}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 gap-2 rounded-full px-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {loadingUser ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      `${profile?.firstName?.[0] ?? '?'}${profile?.lastName?.[0] ?? ''}`
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col items-start text-left">
                  <span className="text-[13px] font-medium leading-tight">
                    {loadingUser ? "Loading..." : `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()}
                  </span>
                  <span className="text-[11px] text-muted-foreground leading-tight">
                    {/* Live profile role name when loaded; fall back to localStorage roleName */}
                    {loadingUser ? "Please wait" : (profile?.role?.name ?? roleName ?? "User")}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link to="/dashboard/profile" className="w-full cursor-pointer">Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/dashboard/settings" className="w-full cursor-pointer">Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* ⌘K navigation palette */}
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent className="overflow-hidden p-0 sm:max-w-md top-[30%] translate-y-0">
            <Command>
              <CommandInput placeholder="Go to page…" />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                {["Navigate", "Audit", "Admin"].map((group) => {
                  const items = searchTargets.filter((t) => t.group === group);
                  if (items.length === 0) return null;
                  return (
                    <CommandGroup key={group} heading={group}>
                      {items.map((item) => (
                        <CommandItem
                          key={item.path}
                          value={`${item.group} ${item.label}`}
                          onSelect={() => {
                            setSearchOpen(false);
                            navigate(item.path);
                          }}
                        >
                          <item.icon className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                          {item.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </DialogContent>
        </Dialog>

        {/* Page content — keyed on theme so ApexCharts options (which read the
            active theme imperatively via getChartColors()) rebuild on toggle.
            Bottom padding on mobile clears the fixed tab bar. */}
        <main className="flex-1 overflow-y-auto">
          <div key={theme} className="mx-auto w-full max-w-[1440px] px-4 py-4 pb-24 md:py-6 md:pb-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* Bottom tab bar — primary navigation on small screens (<md).
            First four visible top-level sections + a Menu tab that opens the
            full drawer (covers Audit/Admin and anything past four). */}
        <nav
          aria-label="Primary"
          className="fixed inset-x-0 bottom-0 z-40 flex border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          {filteredNav
            .filter((e): e is NavItem => !isNavGroup(e))
            .slice(0, 4)
            .map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 no-underline transition-colors",
                    active ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" strokeWidth={active ? 2 : 1.75} />
                  <span className="text-[10px] font-medium leading-none">{item.label}</span>
                </Link>
              );
            })}
          <button
            onClick={() => setMobileOpen(true)}
            className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 text-muted-foreground transition-colors cursor-pointer"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
            <span className="text-[10px] font-medium leading-none">Menu</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
